// React
import { useEffect, useState } from 'react';

// Store
import useSiteStore from '../../store/siteStore.js';

// Hooks
import { useAppTheme } from '../../hooks/useAppTheme.js';

// Components
import { AccessTimeOutlined, AddOutlined, CloseOutlined, DeleteOutlined, DescriptionOutlined, FitnessCenterOutlined, RemoveOutlined, SpeedOutlined } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Grid, IconButton, InputAdornment, Modal, Paper, Typography, Tooltip } from '@mui/material';
import CustomFormControl from '../custom/FormControl.js';
import { CustomSelect, CustomMenuItem, getMenuProps } from '../custom/Select.js';
import CustomTextField from '../custom/TextField.js';
import CustomSlider from '../custom/Slider.js';
import CustomSnackbar from '../custom/CustomSnackbar.js';

// Style
import { blue, green, grey, red, yellow, orange } from '@mui/material/colors/index.js';
import { darkTheme } from '../../styles/colorSchemes/index.js';

// Other 
import { addTrainingSession, deleteTrainingSessionByID } from '../../service/httpService/trainingSession/index.js';
import { formatDate } from '../../utils/index.js';

const EditTrainingSession = (props) => {
    const { athlete, calendarWeek, isAthlete, onClose, open, setSelectedSession, trainingSession } = props;
    const { year, removeTrainingSession, setSuccessMessage: setGlobalSuccessMessage, setFailMessage: setGlobalFailMessage, setSnackbarOpenSuccess: setGlobalSnackbarOpen, setSnackbarOpenFail: setGlobalSnackbarFail, updateTrainingSession } = useSiteStore();
    const { isDarkMode } = useAppTheme();

    const [openFail, setOpenFail] = useState(false);
    const [failMessage, setFailMessage] = useState('');
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [openSuccess, setOpenSuccess] = useState(false);
    const [successMessage] = useState('');

    const edit = () => {
        if (trainingSession.trainingFocus <= 0) {
            setOpenFail(true);
            setFailMessage('Fehlerhafte Trainingseinheit: Fokus!');
            return;
        }
        if (trainingSession.trainingFocus !== 8 && trainingSession.trainingFocus !== 7 && trainingSession.trainingFocus !== 9) {
            if (trainingSession.duration <= 0) {
                setOpenFail(true);
                setFailMessage('Fehlerhafte Trainingseinheit: Dauer!');
                return;
            }
            if (trainingSession.intensity <= 0) {
                setOpenFail(true);
                setFailMessage('Fehlerhafte Trainingseinheit: Intensität!');
                return;
            }
        }

        addTrainingSession(athlete.profile.email, year, calendarWeek, trainingSession.dayOfWeek, {
            trainingFocus: trainingSession.trainingFocus,
            description: trainingSession.description,
            duration: trainingSession.duration,
            intensity: trainingSession.intensity,
            trainingsessionid: trainingSession.trainingsessionid
        })
            .then(() => {
                // 1. Optimistic UI Update: Liste sofort aktualisieren
                updateTrainingSession({
                    trainingFocus: trainingSession.trainingFocus,
                    description: trainingSession.description,
                    duration: trainingSession.duration,
                    intensity: trainingSession.intensity,
                    trainingsessionid: trainingSession.trainingsessionid,
                    dayOfWeek: trainingSession.dayOfWeek,
                    date: trainingSession.date
                });
                setGlobalSuccessMessage('Trainingseinheit erfolgreich aktualisiert!');
                setGlobalSnackbarOpen(true);
                setTimeout(() => {
                    onClose();
                    if (props.onSuccess) props.onSuccess();
                }, 1200);
            })
            .catch(() => {
                setGlobalFailMessage('Aktualisieren der Trainingseinheit fehlgeschlagen!');
                setGlobalSnackbarFail(true);
            });
    }
    /*
        const deleteTrainingSession = () => {
            deleteTSession(athlete.profile.email, year, calendarWeek, trainingSession.dayOfWeek)
                .then(() => {
                    setOpenConfirmation(false);
                    setSuccessMessage('Trainingseinheit erfolgreich gelöscht!');
                    setOpenSuccess(true);
                    setTimeout(() => {
                        setOpenSuccess(false);
                        onClose();
                        if (props.onSuccess) props.onSuccess();
                    }, 1200);
                })
                .catch(() => {
                    setFailMessage('Löschen der Trainingseinheit fehlgeschlagen!');
                    setOpenFail(true);
                });
        }
                */

    useEffect(() => {
        if (trainingSession.trainingFocus === 7 || trainingSession.trainingFocus === 8 || trainingSession.trainingFocus === 9) {
            setSelectedSession({
                dayOfWeek: trainingSession.dayOfWeek,
                trainingFocus: trainingSession.trainingFocus,
                description: trainingSession.description,
                intensity: 0,
                duration: 0,
                date: trainingSession.date,
                trainingsessionid: trainingSession.trainingsessionid
            })
        }
    }, [trainingSession.trainingFocus, trainingSession.dayOfWeek, trainingSession.date, trainingSession.description, setSelectedSession, trainingSession.trainingsessionid]);

    const primaryColor = isDarkMode ? orange[500] : '#1976d2';

    return (
        <Modal
            open={open}
            onClose={(event) => {
                onClose(event);
                setOpenFail(false);
            }}
            sx={{
                display: 'flex',
                pb: 2,
                pt: 2,
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'auto',
                '@media (max-width: 900px)': {
                    alignItems: 'flex-start',
                    p: 0,
                    overflow: 'auto'
                }
            }}
        >
            <Paper
                elevation={8}
                sx={{
                    width: { xs: '95%', sm: '90%', md: '85%' },
                    maxWidth: '800px',
                    p: { xs: 2, sm: 3, md: 4 },
                    borderRadius: 3,
                    border: 'none',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    backgroundColor: isDarkMode ? darkTheme.accordionBg : '#fafafa',
                    position: 'relative',
                    '@media (max-width: 900px)': {
                        width: '100%',
                        minHeight: '100vh',
                        maxHeight: '100vh',
                        borderRadius: 0,
                        m: 0
                    }
                }}
            >
                {/* Close Button */}
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        zIndex: 10,
                        bgcolor: isDarkMode ? darkTheme.headerBg : 'rgba(255, 255, 255, 0.9)',
                        color: isDarkMode ? darkTheme.textSecondary : undefined,
                        '&:hover': {
                            bgcolor: isDarkMode ? darkTheme.cardHover : 'rgba(0, 0, 0, 0.05)',
                            transform: 'rotate(90deg)',
                            transition: 'transform 0.3s ease'
                        }
                    }}
                >
                    <CloseOutlined />
                </IconButton>

                <CustomSnackbar
                    open={openFail}
                    onClose={() => setOpenFail(false)}
                    message={failMessage}
                    severity="error"
                    autoHideDuration={4000}
                />
                <CustomSnackbar
                    open={openSuccess}
                    onClose={() => setOpenSuccess(false)}
                    message={successMessage}
                    severity="success"
                    autoHideDuration={3000}
                />

                <Box sx={{ mb: 2 }}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 600,
                            color: isDarkMode ? darkTheme.textPrimary : grey[800],
                        }}
                    >
                        Trainingseinheit bearbeiten
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: isDarkMode ? darkTheme.textSecondary : grey[500],
                            }}
                        >
                            {formatDate(trainingSession.date)}
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                p: 2,
                                border: `2px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`,
                                borderRadius: 2,
                                backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    borderColor: primaryColor,
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <FitnessCenterOutlined sx={{ mr: 1, color: primaryColor, fontSize: '1.25rem' }} />
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 600,
                                        color: isDarkMode ? darkTheme.textPrimary : grey[700]
                                    }}
                                >
                                    Trainingsfokus
                                </Typography>
                            </Box>

                            <CustomFormControl fullWidth color={primaryColor} isDarkMode={isDarkMode} darkTheme={darkTheme}>
                                <CustomSelect
                                    value={trainingSession.trainingFocus}
                                    disabled={isAthlete}
                                    onChange={(event) =>
                                        setSelectedSession({
                                            dayOfWeek: trainingSession.dayOfWeek,
                                            trainingFocus: event.target.value,
                                            description: trainingSession.description,
                                            intensity: trainingSession.intensity,
                                            duration: trainingSession.duration,
                                            date: trainingSession.date,
                                            trainingsessionid: trainingSession.trainingsessionid
                                        })}
                                    isDarkMode={isDarkMode}
                                    darkTheme={darkTheme}
                                    primaryColor={primaryColor}
                                    MenuProps={getMenuProps(isDarkMode, darkTheme)}
                                >
                                    {!isAthlete && <CustomMenuItem value={1} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Kraft</CustomMenuItem>}
                                    {!isAthlete && <CustomMenuItem value={2} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Ausdauer</CustomMenuItem>}
                                    {!isAthlete && <CustomMenuItem value={3} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Technik</CustomMenuItem>}
                                    {!isAthlete && <CustomMenuItem value={4} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Taktik</CustomMenuItem>}
                                    {!isAthlete && <CustomMenuItem value={5} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Koordination</CustomMenuItem>}
                                    {!isAthlete && <CustomMenuItem value={6} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Schnelligkeit</CustomMenuItem>}
                                    <CustomMenuItem value={7} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Wettkampf/Liga</CustomMenuItem>
                                    <CustomMenuItem value={8} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Ruhetag</CustomMenuItem>
                                    <CustomMenuItem value={9} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Reisetag</CustomMenuItem>
                                    {isAthlete && <CustomMenuItem value={10} isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Sonstige Aktivität</CustomMenuItem>}
                                </CustomSelect>
                            </CustomFormControl>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                p: 1.5,
                                border: `2px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`,
                                borderRadius: 2,
                                backgroundColor: [7, 8, 9, 10].includes(trainingSession.trainingFocus)
                                    ? (isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)')
                                    : (isDarkMode ? darkTheme.cardBg : 'white'),
                                transition: 'all 0.3s ease',
                                '&:hover': [7, 8, 9, 10].includes(trainingSession.trainingFocus) ? {} : {
                                    borderColor: primaryColor,
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4.5 }}>
                                <AccessTimeOutlined sx={{ mr: 1, color: [7, 8, 9, 10].includes(trainingSession.trainingFocus) ? (isDarkMode ? 'rgba(255,255,255,0.3)' : 'action.disabled') : primaryColor, fontSize: '1.25rem' }} />
                                <Typography
                                    variant="body1"
                                    sx={{
                                        mt: 0.25,
                                        fontWeight: 600,
                                        color: [7, 8, 9, 10].includes(trainingSession.trainingFocus)
                                            ? (isDarkMode ? darkTheme.textSecondary : grey[400])
                                            : (isDarkMode ? darkTheme.textPrimary : grey[700])
                                    }}
                                >
                                    Geplante Dauer
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <IconButton
                                    size="small"
                                    disabled={[7, 8, 9, 10].includes(trainingSession.trainingFocus) || trainingSession.duration <= 0}
                                    onClick={() => setSelectedSession({
                                        dayOfWeek: trainingSession.dayOfWeek,
                                        trainingFocus: trainingSession.trainingFocus,
                                        description: trainingSession.description,
                                        intensity: trainingSession.intensity,
                                        duration: Math.max(0, Number(trainingSession.duration) - 1),
                                        date: trainingSession.date,
                                        trainingsessionid: trainingSession.trainingsessionid
                                    })}
                                    sx={{
                                        backgroundColor: isDarkMode ? darkTheme.headerBg : grey[200],
                                        width: 32,
                                        height: 32,
                                        color: isDarkMode ? darkTheme.textPrimary : undefined,
                                        '&:hover': {
                                            backgroundColor: isDarkMode ? darkTheme.cardHover : grey[300]
                                        },
                                        '&.Mui-disabled': {
                                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : grey[100],
                                            color: isDarkMode ? darkTheme.textSecondary : undefined
                                        }
                                    }}
                                >
                                    <RemoveOutlined sx={{ fontSize: '1rem' }} />
                                </IconButton>

                                <CustomTextField
                                    disabled={[7, 8, 9, 10].includes(trainingSession.trainingFocus)}
                                    onChange={(event) =>
                                        setSelectedSession({
                                            dayOfWeek: trainingSession.dayOfWeek,
                                            trainingFocus: trainingSession.trainingFocus,
                                            description: trainingSession.description,
                                            intensity: trainingSession.intensity,
                                            duration: (event.target.value >= 0 && event.target.value.length <= 3) ? Number(event.target.value) : trainingSession.duration,
                                            date: trainingSession.date,
                                            trainingsessionid: trainingSession.trainingsessionid
                                        })
                                    }
                                    value={trainingSession.duration}
                                    variant="outlined"
                                    size="small"
                                    inputProps={{
                                        style: {
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            fontSize: '1.1rem'
                                        }
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Typography variant="body2" sx={{ color: grey[600], fontSize: '0.8rem' }}>
                                                    Min
                                                </Typography>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            bgcolor: isDarkMode ? darkTheme.headerBg : undefined,
                                            '&:hover:not(.Mui-disabled) fieldset': {
                                                borderColor: primaryColor,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: primaryColor,
                                            },
                                            '& fieldset': {
                                                borderColor: isDarkMode ? darkTheme.cardBorder : grey[300]
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            color: isDarkMode ? darkTheme.textPrimary : undefined,
                                        }
                                    }}
                                />

                                <IconButton
                                    size="small"
                                    disabled={[7, 8, 9, 10].includes(trainingSession.trainingFocus)}
                                    onClick={() => setSelectedSession({
                                        dayOfWeek: trainingSession.dayOfWeek,
                                        trainingFocus: trainingSession.trainingFocus,
                                        description: trainingSession.description,
                                        intensity: trainingSession.intensity,
                                        duration: Number(trainingSession.duration) + 1,
                                        date: trainingSession.date,
                                        trainingsessionid: trainingSession.trainingsessionid
                                    })}
                                    sx={{
                                        backgroundColor: isDarkMode ? darkTheme.headerBg : grey[200],
                                        width: 32,
                                        height: 32,
                                        color: isDarkMode ? darkTheme.textPrimary : undefined,
                                        '&:hover': {
                                            backgroundColor: isDarkMode ? darkTheme.cardHover : grey[300]
                                        },
                                        '&.Mui-disabled': {
                                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : grey[100],
                                            color: isDarkMode ? darkTheme.textSecondary : undefined
                                        }
                                    }}
                                >
                                    <AddOutlined sx={{ fontSize: '1rem' }} />
                                </IconButton>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Box
                            sx={{
                                p: 1.5,
                                border: `2px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`,
                                borderRadius: 2,
                                backgroundColor: [7, 8, 9, 10].includes(trainingSession.trainingFocus)
                                    ? (isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)')
                                    : (isDarkMode ? darkTheme.cardBg : 'white'),
                                transition: 'all 0.3s ease',
                                '&:hover': [7, 8, 9, 10].includes(trainingSession.trainingFocus) ? {} : {
                                    borderColor: primaryColor,
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <SpeedOutlined sx={{ mr: 1, color: [7, 8, 9, 10].includes(trainingSession.trainingFocus) ? (isDarkMode ? 'rgba(255,255,255,0.3)' : 'action.disabled') : primaryColor, fontSize: '1.25rem' }} />
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 600,
                                        color: [7, 8, 9, 10].includes(trainingSession.trainingFocus)
                                            ? (isDarkMode ? darkTheme.textSecondary : grey[400])
                                            : (isDarkMode ? darkTheme.textPrimary : grey[700])
                                    }}
                                >
                                    Geplante Intensität: {trainingSession.intensity}/10
                                </Typography>
                            </Box>

                            <Box sx={{ position: 'relative', width: '95%', marginLeft: 1.2 }}>
                                <CustomSlider
                                    getAriaValueText={(value) => {
                                        return `${value}`
                                    }}
                                    value={trainingSession.intensity}
                                    onChange={(event, newValue) => {
                                        setSelectedSession({
                                            dayOfWeek: trainingSession.dayOfWeek,
                                            trainingFocus: trainingSession.trainingFocus,
                                            description: trainingSession.description,
                                            intensity: newValue,
                                            duration: trainingSession.duration,
                                            date: trainingSession.date,
                                            trainingsessionid: trainingSession.trainingsessionid
                                        })
                                    }}
                                    disabled={[7, 8, 9, 10].includes(trainingSession.trainingFocus)}
                                    valueLabelDisplay="auto"
                                    step={1}
                                    max={10}
                                    marks={[
                                        { value: 0, label: '0' },
                                        { value: 5, label: '5' },
                                        { value: 10, label: '10' }
                                    ]}
                                    sx={{
                                        height: 8,
                                        '& .MuiSlider-thumb': {
                                            width: 24,
                                            height: 24,
                                            backgroundColor: 'white',
                                            border: `3px solid ${primaryColor}`,
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                            zIndex: 3,
                                            '&:hover, &.Mui-focusVisible': {
                                                boxShadow: `0 0 0 8px ${primaryColor}20`,
                                            },
                                            '&.Mui-active': {
                                                boxShadow: `0 0 0 12px ${primaryColor}30`,
                                            },
                                        },
                                        '& .MuiSlider-track': {
                                            background: `linear-gradient(90deg, ${green[400]} 0%, ${yellow[500]} 50%, ${red[500]} 100%)`,
                                            backgroundSize: `${1000 / Math.max(trainingSession.intensity, 1)}% 100%`,
                                            border: 'none',
                                            zIndex: 2,
                                        },
                                        '& .MuiSlider-rail': {
                                            backgroundColor: isDarkMode ? darkTheme.cardBorder : grey[200],
                                            opacity: 1,
                                            zIndex: 1,
                                        },
                                        '& .MuiSlider-valueLabel': {
                                            backgroundColor: primaryColor,
                                            borderRadius: '8px',
                                            padding: '4px 8px',
                                            fontSize: '0.875rem',
                                            fontWeight: 600,
                                        },
                                        '& .MuiSlider-mark': {
                                            backgroundColor: grey[400],
                                            height: 8,
                                            width: 2,
                                            borderRadius: 1,
                                        },
                                        '& .MuiSlider-markActive': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        },
                                        '& .MuiSlider-markLabel': {
                                            fontSize: '0.75rem',
                                            color: isDarkMode ? darkTheme.textSecondary : grey[500],
                                            fontWeight: 500,
                                        },
                                        '&.Mui-disabled': {
                                            '& .MuiSlider-thumb': {
                                                backgroundColor: isDarkMode ? darkTheme.cardBg : grey[300],
                                                border: `3px solid ${isDarkMode ? 'rgba(255,255,255,0.3)' : grey[400]}`,
                                            },
                                            '& .MuiSlider-track': {
                                                background: isDarkMode ? 'rgba(255,255,255,0.1)' : grey[300],
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Box
                            sx={{
                                p: 2,
                                border: `2px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`,
                                borderRadius: 2,
                                backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    borderColor: primaryColor,
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <DescriptionOutlined sx={{ mr: 1, color: primaryColor }} />
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 600,
                                        color: isDarkMode ? darkTheme.textPrimary : grey[700]
                                    }}
                                >
                                    Beschreibung
                                </Typography>
                            </Box>

                            <CustomTextField
                                fullWidth
                                multiline
                                type='text'
                                variant='outlined'
                                placeholder="Trainingsdetails eingeben..."
                                disabled={isAthlete}
                                value={trainingSession.description}
                                onChange={(event) => setSelectedSession({
                                    dayOfWeek: trainingSession.dayOfWeek,
                                    trainingFocus: trainingSession.trainingFocus,
                                    description: event.target.value,
                                    intensity: trainingSession.intensity,
                                    duration: trainingSession.duration,
                                    date: trainingSession.date,
                                    trainingsessionid: trainingSession.trainingsessionid
                                })}
                                minRows={4}
                                maxRows={4}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: isDarkMode ? darkTheme.headerBg : undefined,
                                        '&:hover fieldset': {
                                            borderColor: primaryColor,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: primaryColor,
                                        },
                                        '& fieldset': {
                                            borderColor: isDarkMode ? darkTheme.cardBorder : grey[300]
                                        }
                                    },
                                    '& .MuiInputBase-input': {
                                        color: isDarkMode ? darkTheme.textPrimary : undefined,
                                    }
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>

                <Divider sx={{ mt: 3, mb: 1, border: 'none' }} />

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>

                    <Tooltip title="Trainingseinheit löschen">
                        <Box sx={{ display: 'inline-flex' }}>
                            <IconButton
                                onClick={() => setOpenConfirmation(true)}
                                sx={{
                                    color: red[600],
                                    '&:hover': {
                                        backgroundColor: red[50],
                                        transform: 'scale(1.1)',
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <DeleteOutlined />
                            </IconButton>
                        </Box>
                    </Tooltip>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', flex: 1, mr: 5 }}>
                        {!isAthlete &&
                            <Button
                                onClick={edit}
                                variant="contained"
                                sx={{
                                    minWidth: 140,
                                    height: 44,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    color: 'white',
                                    backgroundColor: primaryColor,
                                    boxShadow: isDarkMode
                                        ? '0 2px 8px rgba(255, 152, 0, 0.3)'
                                        : '0 2px 8px rgba(25, 118, 210, 0.3)',
                                    '&:hover': {
                                        backgroundColor: isDarkMode ? orange[600] : blue[700],
                                        boxShadow: isDarkMode
                                            ? '0 4px 12px rgba(255, 152, 0, 0.4)'
                                            : '0 4px 12px rgba(25, 118, 210, 0.4)',
                                        transform: 'translateY(-1px)',
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                Aktualisieren
                            </Button>
                        }
                    </Box>
                </Box>

                <Dialog
                    open={openConfirmation}
                    onClose={() => setOpenConfirmation(false)}
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            p: 1
                        }
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem', color: grey[800] }}>
                        Löschen bestätigen
                    </DialogTitle>

                    <DialogContent>
                        <DialogContentText sx={{ color: grey[700] }}>
                            Soll diese Trainingseinheit ({formatDate(trainingSession.date)}) gelöscht werden?
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions sx={{ p: 2, gap: 1 }}>
                        <Button
                            sx={{
                                color: 'white',
                                backgroundColor: red[600],
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 3,
                                '&:hover': {
                                    backgroundColor: red[700],
                                    transform: 'translateY(-1px)',
                                },
                                transition: 'all 0.2s ease'
                            }}
                            onClick={() =>
                                deleteTrainingSessionByID(athlete.profile.email, trainingSession.trainingsessionid)
                                    .then(() => {
                                        removeTrainingSession(trainingSession.trainingsessionid);
                                        setGlobalSuccessMessage('Trainingseinheit erfolgreich gelöscht!');
                                        setGlobalSnackbarOpen(true);
                                        setOpenSuccess(false);
                                        onClose();
                                        setOpenConfirmation(false)
                                        if (props.onSuccess) props.onSuccess();
                                    })
                            }>
                            Löschen
                        </Button>

                        <Button
                            sx={{
                                color: grey[700],
                                borderColor: grey[400],
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                                px: 3,
                                '&:hover': {
                                    backgroundColor: grey[50],
                                    borderColor: grey[600],
                                },
                                transition: 'all 0.2s ease'
                            }}
                            variant="outlined"
                            onClick={() => setOpenConfirmation(false)}>
                            Abbrechen
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Modal>
    )
}

export default EditTrainingSession;